import { Link } from "react-router-dom";
import { Sparkles, Heart, Leaf, Scissors } from "lucide-react";
import { LOGO_URL } from "../data/products";
import { useReveal } from "../hooks/useReveal";
import { useContent } from "../lib/content";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { absUrl } from "../components/admin/ImageUploader";

const VALUE_ICONS = [Scissors, Leaf, Heart];

export default function About() {
  const heroRef = useReveal();
  const storyRef = useReveal();
  const valuesRef = useReveal();
  const ctaRef = useReveal();
  const { content } = useContent();
  const about = content?.about || {};
  const seo = content?.seo || {};
  useDocumentMeta({
    title: `About · ${seo.site_title || "Flownté"}`,
    description: about.hero_paragraph,
    image: seo.og_image,
  });

  const values = (about.values || []).map((v, i) => ({ ...v, icon: VALUE_ICONS[i % VALUE_ICONS.length] }));

  return (
    <div data-testid="about-page" className="pb-24">
      <section
        ref={heroRef}
        className="reveal relative py-20 md:py-28 bg-gradient-to-b from-blush-50 via-cream-100 to-cream-100 overflow-hidden"
      >
        <div className="blob bg-blush-200 w-[420px] h-[420px] -top-24 -left-20" />
        <div className="blob bg-blush-100 w-[360px] h-[360px] bottom-0 right-0" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <img
            src={LOGO_URL}
            alt="Flownté logo"
            className="h-24 w-24 rounded-full object-cover mx-auto ring-1 ring-blush-200"
          />
          <span className="mt-6 block font-script text-4xl text-blush-600">{about.hero_eyebrow}</span>
          <h1 className="font-heading text-4xl md:text-6xl text-ink-900 mt-2 leading-tight">
            {about.hero_title_lead}
            <br />
            <span className="italic text-blush-600">{about.hero_title_accent}</span>
          </h1>
          <p className="mt-6 font-body text-lg text-ink-700 max-w-2xl mx-auto leading-relaxed">
            {about.hero_paragraph}
          </p>
        </div>
      </section>

      <section ref={storyRef} className="reveal relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img
              src={absUrl(about.image_left) || "https://images.unsplash.com/photo-1763294631806-b414045e086c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"}
              alt=""
              className="rounded-[2rem] aspect-[4/5] object-cover shadow-blush"
            />
            <img
              src={absUrl(about.image_right) || "https://images.pexels.com/photos/31808646/pexels-photo-31808646.jpeg?auto=compress&cs=tinysrgb&w=1200"}
              alt=""
              className="rounded-[2rem] aspect-[4/5] object-cover shadow-blush mt-10"
            />
          </div>
          <div>
            <span className="font-script text-3xl text-blush-400">{about.story_eyebrow}</span>
            <h2 className="font-heading text-3xl md:text-5xl text-ink-900 mt-1">
              {about.story_title}
            </h2>
            {(about.story_paragraphs || []).map((p, i) => (
              <p key={i} className="mt-6 font-body text-ink-700 leading-relaxed">
                {p}
              </p>
            ))}
            <Link to="/shop" className="btn-maroon mt-8 inline-flex items-center gap-2">
              Explore our pieces
              <Sparkles size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <section
        ref={valuesRef}
        className="reveal relative py-20 md:py-28 bg-gradient-to-b from-cream-100 via-blush-50/60 to-cream-100"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <span className="font-script text-3xl text-blush-400">what we hold close</span>
            <h2 className="font-heading text-3xl md:text-5xl text-ink-900 mt-1">
              Our promises
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 stagger">
            {values.map((v, i) => (
              <div
                key={i}
                className="rounded-3xl bg-white border border-blush-100 p-8 shadow-sm hover:shadow-blush transition-shadow duration-500"
              >
                <div className="h-12 w-12 rounded-full bg-blush-100 grid place-items-center text-blush-600">
                  <v.icon size={18} strokeWidth={1.4} />
                </div>
                <h3 className="font-heading text-2xl text-ink-900 mt-5">{v.title}</h3>
                <p className="mt-3 font-body text-ink-700 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section ref={ctaRef} className="reveal relative py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="font-script text-4xl text-blush-600">elegance in motion</span>
          <h3 className="mt-3 font-heading text-3xl md:text-5xl text-ink-900">
            Step into a softer everyday.
          </h3>
          <p className="mt-5 font-body text-ink-700 max-w-xl mx-auto">
            Begin with a single kurti. Stay for the craft, the comfort, and the quiet joy of
            getting dressed.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/shop" className="btn-maroon">
              Shop the collection
            </Link>
            <Link to="/contact" className="btn-ghost-rose">
              Visit our atelier
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
